import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils'

/**
 * shadcn-style Tooltip primitives wrapping `@radix-ui/react-tooltip`.
 * The app's <TooltipProvider> sits at the root of App.tsx with a
 * site-wide `delayDuration={200}`, so all consumers get fast hover
 * tooltips without needing their own provider.
 *
 * Usage:
 *   <Tooltip>
 *     <TooltipTrigger asChild><button>...</button></TooltipTrigger>
 *     <TooltipContent>Label</TooltipContent>
 *   </Tooltip>
 *
 * Use this in place of `title=""` native attributes (which have a
 * browser-controlled ~700ms delay we can't override).
 */
const TooltipProvider = TooltipPrimitive.Provider
const Tooltip = TooltipPrimitive.Root
const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-md bg-slate-900 px-2.5 py-1 text-xs text-slate-50 shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
